import { useDailyReportDetailSectionQuery } from 'generated/graphql';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React from 'react';

export interface IData {
  id: string;
  description: string;
  reportDate: DateTime | null;
  approvedAt: DateTime | null;
  approvedByName: string;
  inspections: {
    id: string;
    createdAt: DateTime;
    inspectedSystem: string;
    dateInspected: DateTime | null;
    inspectedByName: string;
    description: string;
    inspectionResult: {
      name: string;
      displayName: string;
    };
  }[];
  photos: {
    id: string;
    url: string;
  }[];
  incidents: {
    id: string;
    createdOn: DateTime;
    issueDate: DateTime;
    personsInvolved: {
      name: string;
      imgUrl: string;
    }[];
    description: string;
  }[];
  deliveries: {
    id: string;
    attachments: {
      id: string;
      url: string;
      name: string;
      fileSize: string;
    }[];
    createdTimestamp: DateTime;
    receivedDate: DateTime | null;
    name: string;
    description: string;
    quantity: string;
    damageDescription: string;
    issueDescription: string;
    otherDescription: string;
  }[];
  workLog: {
    id: string;
    dateWorked: DateTime;
    personName: string;
    hoursWorked: string;
    onSiteWorkers: string;
    description: string;
    trade: string;
  }[];
}

export function useQuery(id: string) {
  const tuple = useDailyReportDetailSectionQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.dailyReport?.id ?? '',
        description: tuple.data?.dailyReport?.description ?? '',
        reportDate: tuple.data?.dailyReport?.reportDate ?? null,
        approvedAt: tuple.data?.dailyReport?.approvedAt ?? null,
        approvedByName: tuple.data?.dailyReport?.approvedBy?.fullName ?? '',
        inspections: tuple.data?.dailyReport?.inspections?.map((node) => ({
          id: node?.id ?? '',
          createdAt: node?.createdAt ?? DateTime.now(),
          dateInspected: node?.dateInspected ?? DateTime.now(),
          description: node?.description ?? '',
          inspectedByName: node?.inspectedBy.fullName ?? '',
          inspectedSystem: node?.inspectedSystem ?? '',
          inspectionResult: {
            displayName: node.inspectionResult?.displayName ?? '',
            name: node.inspectionResult?.name ?? ''
          }
        })) ?? [],
        photos: tuple.data?.dailyReport?.photos.map((node) => ({
          id: node.id ?? '',
          url: node.apiFile.url ?? ''
        })) ?? [],
        incidents: tuple.data?.dailyReport?.incidents?.map((node) => ({
          id: node?.id ?? '',
          createdOn: node.createdOn ?? DateTime.now(),
          issueDate: node.issueDate ?? DateTime.now(),
          personsInvolved: node?.personsInvolved.map((node) => ({
            name: node.fullName,
            imgUrl: node.profilePicture?.url ?? ''
          })) ?? [],
          description: node?.description ?? ''
        })) ?? [],
        deliveries: tuple.data?.dailyReport?.deliveries?.map((node) => ({
          id: node?.id ?? '',
          attachments: node?.attachments.map((rec) => ({
            id: rec.id ?? '',
            name: '',
            url: rec.mimeType.includes('image') ? rec.url : '',
            fileSize: ''
          })) ?? [],
          createdTimestamp: node?.createdTimestamp ?? DateTime.now(),
          receivedDate: node?.receivedDate ?? null,
          name: node?.name ?? '',
          description: node?.description ?? '',
          quantity: _.toString(node?.quantity) ?? '0',
          damageDescription: node?.damageDescription ?? '',
          issueDescription: node?.issueDescription ?? '',
          otherDescription: node?.otherDescription ?? ''
        })) ?? [],
        workLog: tuple.data?.dailyReport?.workLog?.map((node) => ({
          id: node.id,
          dateWorked: node.dateWorked ?? null,
          hoursWorked: _.toString(node.hoursWorked),
          personName: node.person.fullName,
          description: __createWorkEventDescription(node),
          onSiteWorkers: _.toString(node.onSiteSubContractors),
          trade: node.trade?.displayName ?? '-'
        })) ?? []
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
function __createWorkEventDescription(node: { onSiteSubContractors: number; description: string; hoursWorked: number }): string {
  const contractorsStr = node.onSiteSubContractors !== 0
    ? `${node.onSiteSubContractors} on site workers`
    : '';

  let hoursStr = '';
  if (contractorsStr === '') {
    hoursStr = '';
  } else {
    hoursStr = ` were on worked for ${node.hoursWorked} hour(s).\n`;

  }
  const workPerformedStr = 'Work performed: ';

  const desc = _.join([
    contractorsStr,
    hoursStr,
    workPerformedStr,
    node.description
  ], '');
  return desc;
}

